import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, message, Tag, Modal, Space, Tooltip } from 'antd';
import { EditOutlined, SaveOutlined, ReloadOutlined, FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined, LinkOutlined, SyncOutlined } from '@ant-design/icons';
import axios from 'axios';
import authService from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const { TextArea } = Input;

export default function PdfTextManager() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [syncing, setSyncing] = useState(false);

  const handleTtourSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_BASE}/ttour/sync`, {}, { headers: authService.getAuthHeaders() });
      const d = res.data.data;
      message.success(
        `同步完成：新增产品 ${d.productsCreated}，新增出团 ${d.toursCreated}，跳过 ${d.productsSkipped + d.toursSkipped}${d.errors.length ? `，${d.errors.length} 个错误` : ''}`
      );
    } catch (err) {
      message.error(err.response?.data?.error || '同步失败');
    } finally {
      setSyncing(false);
    }
  };

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/ttour/pdf-docs`, { headers: authService.getAuthHeaders() });
      if (res.data.success) setDocs(res.data.data);
    } catch (err) {
      message.error(err.response?.data?.error || '无法加载PDF列表，请确认Ttour数据库可访问');
    }
    setLoading(false);
  };

  useEffect(() => { fetchDocs(); }, []);

  const handleEdit = async (filename) => {
    try {
      const res = await axios.get(`${API_BASE}/ttour/pdf-docs/${encodeURIComponent(filename)}`, { headers: authService.getAuthHeaders() });
      if (res.data.success) {
        setEditDoc(res.data.data);
        setEditText(res.data.data.raw_text || '');
      }
    } catch (err) {
      message.error('加载文档内容失败');
    }
  };

  const handleSave = async () => {
    if (!editDoc) return;
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE}/ttour/pdf-docs/${encodeURIComponent(editDoc.pdf_filename)}`,
        { raw_text: editText },
        { headers: authService.getAuthHeaders() }
      );
      message.success('已保存');
      setEditDoc(null);
      fetchDocs();
    } catch (err) {
      message.error(err.response?.data?.error || '保存失败');
    }
    setSaving(false);
  };

  const filteredDocs = searchText
    ? docs.filter(d => d.pdf_filename.toLowerCase().includes(searchText.toLowerCase()))
    : docs;

  const columns = [
    {
      title: '文件名',
      dataIndex: 'pdf_filename',
      key: 'pdf_filename',
      ellipsis: true,
      render: (v) => <Tooltip title={v}><span><FileTextOutlined style={{ marginRight: 6 }} />{v}</span></Tooltip>
    },
    {
      title: '状态',
      dataIndex: 'needs_ocr',
      key: 'needs_ocr',
      width: 80,
      align: 'center',
      render: (v) => v === 0
        ? <Tag icon={<CheckCircleOutlined />} color="success">正常</Tag>
        : <Tag icon={<CloseCircleOutlined />} color="error">失败</Tag>
    },
    {
      title: '页数',
      dataIndex: 'page_count',
      key: 'page_count',
      width: 60,
      align: 'center'
    },
    {
      title: '字数',
      dataIndex: 'textLen',
      key: 'textLen',
      width: 80,
      align: 'right',
      render: (v) => (v || 0).toLocaleString()
    },
    {
      title: '解析时间',
      dataIndex: 'parsed_at',
      key: 'parsed_at',
      width: 160,
    },
    {
      title: '操作',
      key: 'actions',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="编辑">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record.pdf_filename)} />
        </Tooltip>
      )
    }
  ];

  return (
    <Card
      title="PDF 文本管理（Ttour）"
      extra={
        <Space>
          <Input.Search
            placeholder="搜索文件名"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: 240 }}
          />
          <Button icon={<SyncOutlined spin={syncing} />} loading={syncing} onClick={handleTtourSync}>从 Ttour 同步</Button>
          <Button icon={<ReloadOutlined />} onClick={fetchDocs}>刷新</Button>
        </Space>
      }
    >
      <Table
        dataSource={filteredDocs}
        columns={columns}
        rowKey="pdf_filename"
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="small"
      />

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
            <span>{editDoc ? `编辑 — ${editDoc.pdf_filename}` : '编辑'}</span>
            {editDoc?.pdf_url && (
              <a href={editDoc.pdf_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 'normal', whiteSpace: 'nowrap', marginLeft: 16 }}>
                <LinkOutlined style={{ marginRight: 4 }} />查看原始PDF
              </a>
            )}
          </div>
        }
        open={!!editDoc}
        onCancel={() => setEditDoc(null)}
        width="90%"
        style={{ top: 20 }}
        footer={[
          <Button key="cancel" onClick={() => setEditDoc(null)}>取消</Button>,
          <Button key="save" type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            保存
          </Button>
        ]}
      >
        {editDoc && (
          <div>
            <div style={{ marginBottom: 8, color: '#888', fontSize: 13 }}>
              页数: {editDoc.page_count || '-'} | 文件大小: {editDoc.byte_size ? (editDoc.byte_size / 1024).toFixed(1) + ' KB' : '-'} | 解析时间: {editDoc.parsed_at || '-'}
            </div>
            <TextArea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              autoSize={{ minRows: 20, maxRows: 50 }}
              style={{ fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}
            />
          </div>
        )}
      </Modal>
    </Card>
  );
}
